import { DownloadIcon } from '@synergy/theme-next';
import {
    FileTypeIcon,
    MarkdownConverter,
    PoolAccordion,
    PoolAccordionDetails,
    PoolAccordionItem,
    PoolAccordionSummary,
    PoolRow,
    PoolTypography,
    useIsMobile,
} from '@synergy/components-next';
import { FormattedMessage } from 'react-intl';
import { List, Stack, styled } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { downloadUrl } from '@synergy/utils';

import { PoolFile } from '../../../models/pool-file';
import { useArticleDetailContext } from '../article-detail';

const StyledStack = styled(Stack)(({ theme }) => ({
    height: 56,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
}));

const DocumentRow = styled(StyledStack)(({ theme }) => ({
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const ListEntry = ({ label, value }: { label: ReactNode; value: ReactNode }): ReactElement => {
    return (
        <StyledStack>
            <strong>{label}</strong>
            <span>{value}</span>
        </StyledStack>
    );
};

export const ArticleDetailDescriptionAccordion = (): ReactElement | null => {
    const { article } = useArticleDetailContext();
    const {
        documents,
        attributes,
        globalTradeItemNumber,
        manufacturer,
        number,
        brand,
        productDescription,
        manufacturerNumber,
    } = article;
    const isMobile = useIsMobile();

    const handleDocumentDownload = (document: PoolFile): void => {
        downloadUrl(document.path, document.title);
    };

    return (
        <PoolAccordion variant={isMobile ? 'medium' : 'large'} activeIndex={productDescription ? 0 : 1}>
            <PoolAccordionItem disabled={!productDescription}>
                <PoolAccordionSummary>
                    <FormattedMessage id="app.article.detail.articleDescription" />
                </PoolAccordionSummary>
                <PoolAccordionDetails sx={{ hyphens: 'auto' }}>
                    <MarkdownConverter value={productDescription} />
                </PoolAccordionDetails>
            </PoolAccordionItem>

            <PoolAccordionItem>
                <PoolAccordionSummary>
                    <FormattedMessage id="app.article.detail.technicalData" />
                </PoolAccordionSummary>
                <PoolAccordionDetails>
                    {number && (
                        <ListEntry
                            label={<FormattedMessage id="app.article.detail.articleNumberAccordion" />}
                            value={number}
                        />
                    )}
                    {globalTradeItemNumber && (
                        <ListEntry
                            label={<FormattedMessage id="app.article.detail.gtin" />}
                            value={globalTradeItemNumber}
                        />
                    )}
                    {brand && <ListEntry label={<FormattedMessage id="app.article.detail.brand" />} value={brand} />}
                    {manufacturer && (
                        <ListEntry
                            label={<FormattedMessage id="app.article.detail.manufacturer" />}
                            value={manufacturer}
                        />
                    )}
                    {manufacturerNumber && (
                        <ListEntry
                            label={<FormattedMessage id="app.article.detail.manufacturerNumber" />}
                            value={manufacturerNumber}
                        />
                    )}
                    {attributes.map(attribute => (
                        <ListEntry key={attribute.attributeId} label={attribute.name} value={attribute.value} />
                    ))}
                </PoolAccordionDetails>
            </PoolAccordionItem>

            <PoolAccordionItem disabled={documents.length === 0}>
                <PoolAccordionSummary>
                    <PoolRow alignItems="baseline" gap={0.5}>
                        <FormattedMessage id="app.common.documents" />
                        &nbsp;
                        <PoolTypography variant="body1">
                            <strong>({documents.length})</strong>
                        </PoolTypography>
                    </PoolRow>
                </PoolAccordionSummary>

                <PoolAccordionDetails sx={{ mr: 0 }}>
                    <List sx={{ p: 0 }}>
                        {documents.map(document => (
                            <DocumentRow key={document.id} onClick={() => handleDocumentDownload(document)}>
                                <PoolRow gap={1.25}>
                                    <FileTypeIcon type={document.type} />
                                    <PoolTypography variant="body1">{document.title}</PoolTypography>
                                </PoolRow>
                                <DownloadIcon />
                            </DocumentRow>
                        ))}
                    </List>
                </PoolAccordionDetails>
            </PoolAccordionItem>
        </PoolAccordion>
    );
};
